import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, Check, X } from 'lucide-react';
import { SCORED_QUESTIONS, TOTAL_QUESTIONS } from './scoring';
import { useScore } from './ScoreContext';
import { createScoreResultUrl } from './scoreResultUrl';
export function Diagnostic() {
  const {
    answers,
    setAnswer,
    answeredCount,
    result,
    isComplete,
    isDiagnosticOpen,
    closeDiagnostic
  } = useScore();
  const [currentIndex, setCurrentIndex] = useState(0);
  const firstUnansweredIndex = useMemo(() => {
    const index = SCORED_QUESTIONS.findIndex(question => answers[question.id] === undefined);
    return index === -1 ? TOTAL_QUESTIONS - 1 : index;
  }, [answers]);
  useEffect(() => {
    if (isDiagnosticOpen) {
      setCurrentIndex(firstUnansweredIndex);
    }
  }, [firstUnansweredIndex, isDiagnosticOpen]);
  if (!isDiagnosticOpen) {
    return null;
  }
  const question = SCORED_QUESTIONS[currentIndex];
  const selectedScore = answers[question.id];
  const canGoBack = currentIndex > 0;
  const canGoForward = currentIndex < TOTAL_QUESTIONS - 1;
  const isLastQuestion = !canGoForward;
  const selectAnswer = (score: number) => {
    setAnswer(question.id, score);
    if (canGoForward) {
      window.setTimeout(() => setCurrentIndex(index => index + 1), 180);
    }
  };
  const showScore = () => {
    if (!isComplete || !result) {
      const missingIndex = SCORED_QUESTIONS.findIndex(item => answers[item.id] === undefined);
      setCurrentIndex(missingIndex === -1 ? TOTAL_QUESTIONS - 1 : missingIndex);
      return;
    }
    window.open(createScoreResultUrl(result), '_blank', 'noopener,noreferrer');
  };
  return <div className="fixed inset-0 z-[60] overflow-y-auto bg-paper px-6 py-6 md:py-10" role="dialog" aria-modal="true" aria-labelledby="diagnostic-title" data-id="element-67">
      <div className="mx-auto max-w-3xl" data-id="element-68">
        <header className="flex items-center justify-between gap-4" data-id="element-69">
          <div data-id="element-70">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-seaglass" data-id="element-71">
              Reader-to-Revenue Score
            </p>
            <p className="mt-1 text-sm font-medium text-text-muted" data-id="element-72">
              {answeredCount} of {TOTAL_QUESTIONS} answered
            </p>
          </div>
          <button type="button" onClick={closeDiagnostic} className="rounded-full p-3 text-text-muted transition-colors hover:bg-paper-dark hover:text-navy focus:outline-none focus:ring-2 focus:ring-seaglass" aria-label="Close diagnostic" data-id="element-73">
            <X className="h-5 w-5" data-id="element-74" />
          </button>
        </header>

        <div className="mt-6 flex gap-1.5" aria-hidden="true" data-id="element-75">
          {SCORED_QUESTIONS.map(item => <span key={item.id} className={`h-1.5 flex-1 rounded-full ${answers[item.id] === undefined ? 'bg-gray-200' : 'bg-seaglass'}`} data-id="element-76" />)}
        </div>

        <main className="py-12 md:py-16" data-id="element-77">
          <p className="text-sm font-medium text-text-muted" data-id="element-78">
            Question {currentIndex + 1} of {TOTAL_QUESTIONS}
          </p>
          <h1 id="diagnostic-title" className="mt-4 max-w-3xl font-serif text-3xl leading-tight text-navy md:text-5xl" data-id="element-79">
            {question.prompt}
          </h1>

          <div className="mt-9 flex flex-col gap-3" data-id="element-80">
            {question.options.map(option => {
            const isSelected = selectedScore === option.score;
            return <button key={option.letter} type="button" aria-pressed={isSelected} onClick={() => selectAnswer(option.score)} className={`flex min-h-[72px] w-full items-start gap-4 rounded-2xl border p-5 text-left transition-all focus:outline-none focus:ring-2 focus:ring-seaglass md:p-6 ${isSelected ? 'border-seaglass bg-seaglass/10 ring-1 ring-seaglass/40' : 'border-gray-200 bg-white hover:border-seaglass/50'}`} data-id="element-81">
                  <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-sm font-semibold ${isSelected ? 'border-seaglass bg-seaglass text-white' : 'border-gray-300 text-navy'}`} data-id="element-82">
                    {isSelected ? <Check className="h-4 w-4" data-id="element-83" /> : option.letter}
                  </span>
                  <span className="leading-relaxed text-text" data-id="element-84">
                    {option.text}
                  </span>
                </button>;
          })}
          </div>

          <div className="mt-10 flex items-center justify-between gap-4" data-id="element-85">
            <button type="button" onClick={() => setCurrentIndex(index => index - 1)} disabled={!canGoBack} className="inline-flex items-center gap-2 rounded-xl px-4 py-3 font-semibold text-navy transition-colors hover:bg-paper-dark disabled:cursor-not-allowed disabled:opacity-40" data-id="element-86">
              <ArrowLeft className="h-4 w-4" data-id="element-87" />
              Back
            </button>

            {isLastQuestion ? <button type="button" onClick={showScore} disabled={selectedScore === undefined || !isComplete} className="inline-flex min-h-[52px] items-center gap-2 rounded-xl bg-sand px-6 py-3 font-bold text-navy transition-colors hover:bg-sand-dark focus:outline-none focus:ring-2 focus:ring-seaglass disabled:cursor-not-allowed disabled:opacity-40" data-id="element-88">
                See my score
                <ArrowRight className="h-4 w-4" data-id="element-89" />
              </button> : <button type="button" onClick={() => setCurrentIndex(index => index + 1)} disabled={selectedScore === undefined} className="inline-flex items-center gap-2 rounded-xl px-4 py-3 font-semibold text-navy transition-colors hover:bg-paper-dark disabled:cursor-not-allowed disabled:opacity-40" data-id="element-90">
                Next question
                <ArrowRight className="h-4 w-4" data-id="element-91" />
              </button>}
          </div>
        </main>
      </div>
    </div>;
}